import React, { useState } from 'react';
import { X } from 'lucide-react';
import { InspectionType, InspectionPriority } from '../types';
import { inspectionService } from '../inspectionService';

interface CreateInspectionModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateInspectionModal: React.FC<CreateInspectionModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [inspectionType, setInspectionType] = useState<InspectionType>('RAMP');
  const [priority, setPriority] = useState<InspectionPriority>('HIGH');
  const [department, setDepartment] = useState('Ground Operations & Ramp Safety');
  const [location, setLocation] = useState('IST / Apron Stand 214');
  const [assetReference, setAssetReference] = useState('TC-TUR');
  const [flightNumber, setFlightNumber] = useState('TK-1923');
  const [scheduledDate, setScheduledDate] = useState(new Date().toISOString().split('T')[0]);
  const [leadInspectorId, setLeadInspectorId] = useState('USR-INSP-01');
  const [leadInspectorName, setLeadInspectorName] = useState('Mert Kilic (Field QA Inspector)');
  const [notes, setNotes] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      alert('Inspection title is required.');
      return;
    }

    try {
      setLoading(true);
      await inspectionService.createInspection({
        title,
        inspectionType,
        priority,
        department,
        location,
        assetReference: assetReference.trim() || undefined,
        flightNumber: flightNumber.trim() || undefined,
        scheduledDate,
        leadInspectorId,
        leadInspectorName,
        notes: notes.trim() || undefined,
        createdBy: 'CURRENT_USER',
      });
      onSuccess();
    } catch (err: any) {
      alert('Failed to create inspection: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-lg">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Schedule Field & Ramp Inspection</h3>
            <p>
              Aircraft, ramp turnaround, dangerous goods, and ground facility safety evaluation.
            </p>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            title="Kapat"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="form-field">
              <label>Inspection Title / Objective *</label>
              <input
                type="text"
                required
                placeholder="e.g. B737-800 Transit Turnaround FOD & Ramp Safety Inspection"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Inspection Type *</label>
                <select
                  value={inspectionType}
                  onChange={(e) => setInspectionType(e.target.value as InspectionType)}
                >
                  <option value="RAMP">RAMP - Apron & Turnaround Safety</option>
                  <option value="AIRCRAFT_PHYSICAL">AIRCRAFT_PHYSICAL - Aircraft Walkaround & Exterior</option>
                  <option value="CABIN_SAFETY">CABIN_SAFETY - Cabin Safety & Emergency Gear</option>
                  <option value="TOOLING">TOOLING - Calibrated Tools & Ground Support</option>
                  <option value="HANGAR_FACILITY">HANGAR_FACILITY - Maintenance Hangar & Facility</option>
                  <option value="DANGEROUS_GOODS">DANGEROUS_GOODS - Cargo & DGR Acceptance</option>
                  <option value="AD_HOC">AD_HOC - Special / Post-Incident Inspection</option>
                </select>
              </div>

              <div className="form-field">
                <label>Priority Level</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as InspectionPriority)}
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="CRITICAL">Critical</option>
                </select>
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Department / Operations Unit *</label>
                <input
                  type="text"
                  required
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Location / Gate / Apron Stand *</label>
                <input
                  type="text"
                  required
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Asset / Aircraft Registration</label>
                <input
                  type="text"
                  placeholder="e.g. TC-TUR or TOOL-CAL-4412"
                  value={assetReference}
                  onChange={(e) => setAssetReference(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Flight Number (If Applicable)</label>
                <input
                  type="text"
                  placeholder="e.g. TK-1923"
                  value={flightNumber}
                  onChange={(e) => setFlightNumber(e.target.value)}
                />
              </div>
            </div>

            <div className="form-grid-3">
              <div className="form-field">
                <label>Scheduled Date *</label>
                <input
                  type="date"
                  required
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Inspector ID</label>
                <input
                  type="text"
                  required
                  value={leadInspectorId}
                  onChange={(e) => setLeadInspectorId(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Lead Inspector Name</label>
                <input
                  type="text"
                  required
                  value={leadInspectorName}
                  onChange={(e) => setLeadInspectorName(e.target.value)}
                />
              </div>
            </div>

            <div className="form-field">
              <label>Special Instructions & Scope Notes</label>
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Specific instructions for inspectors, sampled items, areas of high focus..."
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{ width: 'auto' }}
            >
              {loading ? 'Creating...' : 'Schedule Inspection'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateInspectionModal;
